//#region Asset Imports

import * as moment from 'moment'
import * as _ from 'underscore'

//#endregion

//#region Angular Imports

import { ITimeoutService } from 'angular'

//#endregion

//#region DevExtreme Imports

import DevExpress from 'devextreme'
import { dxDataGridColumn } from 'devextreme/ui/data_grid'

//#endregion

//#region Helper Imports

import { CommonHelper } from '../../../helpers/common-helper'

//#endregion

//#region Helpers

import { Dictionary, FileComponent, ToastHelper, LogHelper, ConvertHelper, TypeHelper, ConfigHelper } from '../../../helpers'
import { KeyValuePair, KeyType } from '../../../helpers/dictionary'
import { DataProviderRequestError, DataProviderRequest } from '../../../data-provider'

//#endregion

//#region Model Imports

import { Enums } from '../../../models/common/enums'
import { OpDataType, OpDataTypeInGroup, OpFile } from '../../../models/core'
import { MeasureCollection, ColumnInfo } from '../../../models/custom'

//#endregion

//#region Service Imports

import { UserSessionService } from '../../../services/user-session'
import { DataTypeGroupService } from '../../../services/data-type'

//#endregion

import { i18n } from '../i18n/common.i18n'
// TODO: remove below
import { ColumnModel, MeasurementModel } from '../../api/api.models'


//#region Types

export interface IMeasurementsService {
    getColumnsAndRowsFromMeasureCollection(dataTypeInColumns: boolean, measureCollections: MeasureCollection[]): JQueryPromise<{ columns: DevExpress.ui.dxDataGridColumn[], rows: { [id: number]: any }[] }>
    measureCollectionsToMeasurementModels(measureCollections: MeasureCollection[]): MeasurementModel[]
}

export type ExportMeasurementsOptions = {
    dateFrom: Date
    dateTo: Date
    deviceIds: number[]
    locationIds: number[]
    meterIds: number[]
    dataTypes: ReadonlyArray<Enums.DataType>
    gridColumns: ReadonlyArray<dxDataGridColumn>
    viewStyle: Enums.MeasurementViewStyle
}

export type GetMeasurementsOptions = {
    dataTypes: ReadonlyArray<Enums.DataType>
    dateFrom: Date
    dateTo: Date

    deviceIds?: number[]
    latest?: boolean
    locationIds?: number[]
    meterIds?: number[]
}

export type GlobalMeasurementsConfiguration = {
    useAggregation: boolean
}

//#endregion

export class MeasurementsService {

    //#region Consts

    private readonly LOG_PREFIX = '[SIMAX.Services.Measurements]'

    private readonly MEASUREMENT_COLUMN_NAME_DATA_TYPE_DICT: Dictionary<Enums.DataType> = Dictionary.createFromObject<Enums.DataType>({
        serialNbrText: Enums.DataType.HELPER_SERIAL_NBR,
        idMeterText: Enums.DataType.HELPER_ID_METER,
        idLocationText: Enums.DataType.HELPER_ID_LOCATION,
        index: Enums.DataType.HELPER_INDEX_NBR,
        date: Enums.DataType.HELPER_TIME,
        dataTypeName: Enums.DataType.HELPER_ID_DATA_TYPE,
        valueText: Enums.DataType.HELPER_VALUE,
        unit: Enums.DataType.HELPER_ID_UNIT,
    })

    //#endregion

    //#region Ctor

    public constructor(private readonly $timeout: ITimeoutService, private readonly userSessionService: UserSessionService, private readonly dataTypeGroupService: DataTypeGroupService) { }

    //#endregion

    public getColumnsAndRowsFromMeasureCollection(dataTypeInColumns: boolean, measureCollections: MeasureCollection[]) {
        if (!measureCollections)
            measureCollections = []

        let result = {
            columns: <DevExpress.ui.dxDataGridColumn[]>[],
            rows: <{ [id: number]: any }[]>[]
        }

        // Date column
        let startDateBoxArray, endDateBoxArray
        result.columns.push({
            dataField: Enums.DataType.HELPER_TIME + '',
            caption: i18n("Common", "Date"),
            dataType: "date",
            sortOrder: 'desc',
            visibleIndex: 1,
            customizeText: (options) => {
                return moment(options.value).format(window['simax'].getDateFormat('DATE_TIME_FORMAT_MOMENTJS'))
            },
            editorOptions: {
                displayFormat: window['simax'].getDateFormat('DATE_FORMAT_DX'),
                onInitialized: e => {
                    var startPlaceholder = i18n("Common", "Start")
                    var endPlaceholder = i18n("Common", "End")
                    var lastOnValueChanged = e.component.option('onValueChanged')

                    if (e.component._options.placeholder == startPlaceholder) {
                        CommonHelper.betweenDateFilterMinVal(e, startPlaceholder, this.$timeout, endDateBoxArray)
                        startDateBoxArray = [e]
                    } else if (e.component._options.placeholder == endPlaceholder) {
                        CommonHelper.betweenDateFilterMaxVal(e, endPlaceholder, this.$timeout, startDateBoxArray)
                        endDateBoxArray = [e]
                    }
                    e.component.option('onValueChanged', e => {
                        lastOnValueChanged(e)
                        CommonHelper.betweenDateFilterMinVal(e, startPlaceholder, this.$timeout, endDateBoxArray)
                        CommonHelper.betweenDateFilterMaxVal(e, endPlaceholder, this.$timeout, startDateBoxArray)
                    })
                }
            }
        })

        // columns for DataTypes in rows
        if (!dataTypeInColumns) {
            // DataType column
            result.columns.push({
                dataField: Enums.DataType.HELPER_ID_DATA_TYPE + '',
                caption: i18n("Common", "DataType"),
                dataType: "string",
                visibleIndex: 4
            })

            // Value column
            result.columns.push({
                dataField: Enums.DataType.HELPER_VALUE + '',
                caption: i18n("Common", "Value"),
                dataType: "number",
                visibleIndex: 5
            })

            // Unit column
            result.columns.push({
                dataField: Enums.DataType.HELPER_ID_UNIT + '',
                caption: i18n("Common", "Unit"),
                dataType: "string",
                alignment: 'left',
                visibleIndex: 6,
                width: 100
            })
        }

        let dataTypesIds: { [id: number]: number | OpDataType } = {}
        let locationsIds: { [id: number]: number } = {}
        let devicesIds: { [id: number]: number } = {}
        let metersIds: { [id: number]: number } = {}

        measureCollections.forEach(measureCollection => {
            if (measureCollection.idDataType)
                dataTypesIds[measureCollection.idDataType] = measureCollection.idDataType
            if (measureCollection.idLocation)
                locationsIds[measureCollection.idLocation] = measureCollection.idLocation
            if (measureCollection.serialNbr)
                devicesIds[measureCollection.serialNbr] = measureCollection.serialNbr
            if (measureCollection.idMeter)
                metersIds[measureCollection.idMeter] = measureCollection.idMeter
        })

        // Location column
        if (Object.keys(locationsIds).length > 1) {
            result.columns.push({
                dataField: Enums.DataType.HELPER_ID_LOCATION + '',
                caption: i18n("Common", "Location"),
                dataType: "string",
                visibleIndex: 1
            })
        }

        // Device column
        if (Object.keys(devicesIds).length > 1) {
            result.columns.push({
                dataField: Enums.DataType.HELPER_SERIAL_NBR + '',
                caption: i18n("Common", "Device"),
                dataType: "string",
                visibleIndex: 2
            })
        }

        // Meter column
        if (Object.keys(metersIds).length > 1) {
            result.columns.push({
                dataField: Enums.DataType.HELPER_ID_METER + '',
                caption: i18n("Common", "Meter"),
                dataType: "string",
                visibleIndex: 3
            })
        }

        return this.userSessionService.getDataProvider().DataType.getDataTypes(Object.keys(dataTypesIds).map(d => +d)).then(dataTypes => {
            dataTypes.forEach(dataType => {
                dataTypesIds[dataType.idDataType] = dataType
                // Data types columns
                if (dataTypeInColumns) {
                    let column = {
                        dataField: dataType.idDataType + '',
                        caption: dataType.toString(),
                        dataType: "number"
                    } as DevExpress.ui.dxDataGridColumn
                    if (dataType.unit)
                        column.caption = column.caption + ' [' + dataType.unit.toString() + ']'
                    result.columns.push(column)
                }
            })

            if (dataTypeInColumns) {
                // rows
                measureCollections.forEach(measureCollection => {
                    measureCollection.measures.forEach(measure => {
                        let filtredRows = result.rows.filter(row => {
                            return row[Enums.DataType.HELPER_ID_LOCATION + ''] == measureCollection.idLocation &&
                                row[Enums.DataType.HELPER_SERIAL_NBR + ''] == measureCollection.serialNbr &&
                                row[Enums.DataType.HELPER_ID_METER + ''] == measureCollection.idMeter &&
                                row[Enums.DataType.HELPER_TIME + ''].getTime() == measure.time.getTime()
                        })
                        if (filtredRows.length > 0) {
                            let row = filtredRows[0]
                            row[measureCollection.idDataType + ''] = measure.value
                        } else {
                            let row: { [id: number]: any } = {}
                            row[Enums.DataType.HELPER_ID_LOCATION + ''] = measureCollection.idLocation
                            row[Enums.DataType.HELPER_SERIAL_NBR + ''] = measureCollection.serialNbr
                            row[Enums.DataType.HELPER_ID_METER + ''] = measureCollection.idMeter
                            row[Enums.DataType.HELPER_TIME + ''] = measure.time
                            row[measureCollection.idDataType + ''] = measure.value
                            result.rows.push(row)
                        }
                    })
                })
            } else {
                measureCollections.forEach(measureCollection => {
                    measureCollection.measures.forEach(measure => {
                        let dataType = <OpDataType>dataTypesIds[measureCollection.idDataType]
                        let row: any = {}
                        row[Enums.DataType.HELPER_ID_LOCATION + ''] = measureCollection.idLocation
                        row[Enums.DataType.HELPER_SERIAL_NBR + ''] = measureCollection.serialNbr
                        row[Enums.DataType.HELPER_ID_METER + ''] = measureCollection.idMeter
                        row[Enums.DataType.HELPER_TIME + ''] = measure.time
                        row[Enums.DataType.HELPER_ID_DATA_TYPE + ''] = dataType.toString()
                        row[Enums.DataType.HELPER_VALUE + ''] = measure.value
                        row[Enums.DataType.HELPER_ID_UNIT + ''] = dataType.unit ? dataType.unit.toString() : ''
                        result.rows.push(row)
                    })
                })
            }

            return result
        })
    }

    public measureCollectionsToMeasurementModels(measureCollections: MeasureCollection[]) {
        let measures: MeasurementModel[] = []
        measureCollections.forEach(c => {
            c.measures.forEach(m => {
                var measurementModel = new MeasurementModel()
                measurementModel.dataType = c.idDataType
                measurementModel.date = m.time
                measurementModel.idLocation = c.idLocation
                measurementModel.idMeter = c.idMeter
                measurementModel.serialNbr = c.serialNbr
                var isDate = CommonHelper.regexMicrosoftDate.test(m.value)
                if (isDate) {
                    measurementModel.value = new Date(moment.parseZone(m.value).format('LLL'))
                } else {
                    measurementModel.value = m.value
                }
                measurementModel.isDate = isDate

                measurementModel['valueText'] = CommonHelper.cellFormatForDifferentValues(measurementModel, null, c.idDataType)

                measures.push(measurementModel)
            })
        })
        return measures
    }

    //#region Chart Type

    public chartTypeToDevExpressType(chartType: Enums.ChartType): string {
        if (chartType != null) {
            const enumString: string = ConvertHelper.enumToString(Enums.ChartType, chartType)

            if (enumString != null) {
                return enumString.toLowerCase()
            }
        }

        return null
    }

    public getSeriesColorByIdDataType(idDataType: number): JQueryPromise<Enums.KnownColorsEnum> {
        const referenceValues: number[] = []

        TypeHelper.iterateEnum(Enums.KnownColorsEnum, (value: number, name: string) => {
            referenceValues.push(value)
        })

        return this.dataTypeGroupService.findReferenceTypeOfGroupsByDataType(
            idDataType,
            Enums.DataTypeGroup.WEBSIMAX_DATA_TYPE_SERIES_COLOR,
            Enums.ReferenceType.Color,
            referenceValues,
        ).then((referenceValue: number) => {
            return referenceValue as Enums.KnownColorsEnum
        })
    }

    public getChartTypeByIdDataType(idDataType: number): JQueryPromise<Enums.ChartType> {
        const referenceValues: number[] = []

        TypeHelper.iterateEnum(Enums.ChartType, (value: number, name: string) => {
            referenceValues.push(value)
        })

        return this.dataTypeGroupService.findReferenceTypeOfGroupsByDataType(
            idDataType,
            Enums.DataTypeGroup.WEBSIMAX_DATA_TYPE_CHART_TYPE,
            Enums.ReferenceType.IdChartType,
            referenceValues,
        ).then((referenceValue: number) => {
            return referenceValue as Enums.ChartType
        })
    }

    //#endregion

    //#region Configuration

    public getGLobalConfiguration(): GlobalMeasurementsConfiguration {
        return {
            useAggregation: ConfigHelper.getValue(Enums.SimaxConfigSection.Charts, ConfigHelper.Key.IsAggregationEnabled) || false,
        };
    }

    //#endregion

    //#region Export

    public exportMeasurements(options: ExportMeasurementsOptions): void {
        if (options != null) {
            this.createColumnInfo(options.gridColumns, options.dataTypes, options.viewStyle)
                .then((columnInfo: ColumnInfo[]) => {
                    this.userSessionService
                        .getDataProvider()
                        .Measurements
                        .exportMeasurements(
                            options.viewStyle,
                            columnInfo,
                            options.dateFrom,
                            options.dateTo,
                            options.locationIds,
                            options.deviceIds,
                            options.meterIds,
                            options.dataTypes
                        )
                        .then((file: OpFile) => {
                            FileComponent.OpenFileSaver(file)
                        })
                        .catch((error: DataProviderRequestError) => {
                            ToastHelper.showError(error.toString())
                            LogHelper.warn(this.LOG_PREFIX, 'Unable to export measurements', error.toString())
                        })
                })
                .catch((error: any) => {
                    LogHelper.warn(this.LOG_PREFIX, 'Unable to prepare column info', error && error.toString())
                })
        }
    }

    private createColumnInfo(gridColumns: ReadonlyArray<dxDataGridColumn>, measurementDataTypes: ReadonlyArray<Enums.DataType>, viewStyle: Enums.MeasurementViewStyle): JQueryPromise<ColumnInfo[]> {
        return this
            .getDataTypesFromGridColumns(gridColumns)
            .then((columnDataTypes: OpDataType[]) => {
                const columnInfos: ColumnInfo[] = this.getCommonColumnInfos(gridColumns, columnDataTypes)

                switch (viewStyle) {
                    case Enums.MeasurementViewStyle.DataInColumns: {
                        columnInfos.push(...this.getDataTypesColumnInfos(gridColumns, measurementDataTypes))

                        break;
                    }

                    case Enums.MeasurementViewStyle.DataInRows: {
                        columnInfos.unshift(this.getDataTypeColumnInfo(gridColumns))
                        columnInfos.push(this.getValueColumnInfo(gridColumns), this.getUnitColumnInfo(gridColumns))

                        break
                    }
                }

                return columnInfos
            })
    }

    private getCommonColumnInfos(gridColumns: ReadonlyArray<dxDataGridColumn>, dataTypes: OpDataType[]): ColumnInfo[] {
        const columnInfos: ColumnInfo[] = []
        const dataTypeDict: Dictionary<OpDataType> = Dictionary.createFromArray(dataTypes, KeyType.Number, (item: OpDataType) => {
            return item.idDataType
        })

        if (dataTypeDict.containsKey(Enums.DataType.HELPER_ID_LOCATION)) {
            const columnInfo = new ColumnInfo(null)

            columnInfo.Name = 'Location'
            columnInfo.IdDataType = Enums.DataType.HELPER_ID_LOCATION
            columnInfo.Description = this.getGridColumnCaptionForDataType(gridColumns, Enums.DataType.HELPER_ID_LOCATION)

            columnInfos.push(columnInfo)
        }

        if (dataTypeDict.containsKey(Enums.DataType.HELPER_ID_METER)) {
            const columnInfo = new ColumnInfo(null)

            columnInfo.Name = 'Meter'
            columnInfo.IdDataType = Enums.DataType.HELPER_ID_METER
            columnInfo.Description = this.getGridColumnCaptionForDataType(gridColumns, Enums.DataType.HELPER_ID_METER)

            columnInfos.push(columnInfo)
        }

        if (dataTypeDict.containsKey(Enums.DataType.HELPER_SERIAL_NBR)) {
            const columnInfo = new ColumnInfo(null)

            columnInfo.Name = 'Device'
            columnInfo.IdDataType = Enums.DataType.HELPER_SERIAL_NBR
            columnInfo.Description = this.getGridColumnCaptionForDataType(gridColumns, Enums.DataType.HELPER_SERIAL_NBR)

            columnInfos.push(columnInfo)
        }

        if (dataTypeDict.containsKey(Enums.DataType.HELPER_INDEX_NBR)) {
            const columnInfo = new ColumnInfo(null)

            columnInfo.Name = 'IndexNbr'
            columnInfo.IdDataType = Enums.DataType.HELPER_INDEX_NBR
            columnInfo.Description = this.getGridColumnCaptionForDataType(gridColumns, Enums.DataType.HELPER_INDEX_NBR)

            columnInfos.push(columnInfo)
        }

        const timeColumnInfo = new ColumnInfo(null)

        timeColumnInfo.Name = 'Time'
        timeColumnInfo.IdDataType = Enums.DataType.HELPER_TIME
        timeColumnInfo.Description = this.getGridColumnCaptionForDataType(gridColumns, Enums.DataType.HELPER_TIME)

        columnInfos.push(timeColumnInfo)

        return columnInfos
    }

    private getDataTypeColumnInfo(gridColumns: ReadonlyArray<dxDataGridColumn>): ColumnInfo {
        const columnInfo = new ColumnInfo(null)

        columnInfo.Name = 'DataType'
        columnInfo.IdDataType = Enums.DataType.HELPER_ID_DATA_TYPE
        columnInfo.Description = this.getGridColumnCaptionForDataType(gridColumns, Enums.DataType.HELPER_ID_DATA_TYPE)

        return columnInfo
    }

    private getDataTypesColumnInfos(gridColumns: ReadonlyArray<dxDataGridColumn>, dataTypes: ReadonlyArray<Enums.DataType>): ColumnInfo[] {
        return _.map(dataTypes, (dataType: Enums.DataType) => {
            const columnInfo = new ColumnInfo(null)

            columnInfo.Name = 'DT_' + dataType + '_0'
            columnInfo.IdDataType = dataType
            columnInfo.Description = this.getGridColumnCaptionForDataType(gridColumns, dataType)

            return columnInfo
        })
    }

    private getValueColumnInfo(gridColumns: ReadonlyArray<dxDataGridColumn>): ColumnInfo {
        const olumnInfo = new ColumnInfo(null)

        olumnInfo.Name = 'Value'
        olumnInfo.IdDataType = Enums.DataType.HELPER_VALUE
        olumnInfo.Description = this.getGridColumnCaptionForDataType(gridColumns, Enums.DataType.HELPER_VALUE)

        return olumnInfo
    }

    private getUnitColumnInfo(gridColumns: ReadonlyArray<dxDataGridColumn>): ColumnInfo {
        const columnInfo = new ColumnInfo(null)

        columnInfo.Name = 'Unit'
        columnInfo.IdDataType = Enums.DataType.HELPER_ID_UNIT
        columnInfo.Description = this.getGridColumnCaptionForDataType(gridColumns, Enums.DataType.HELPER_ID_UNIT)

        return columnInfo
    }

    private getDataTypesFromGridColumns(gridColumns: ReadonlyArray<dxDataGridColumn>): JQueryPromise<OpDataType[]> {
        const dataTypesFromColumns: Enums.DataType[] = _
            .chain(gridColumns as dxDataGridColumn[])
            .map<Enums.DataType>((gridColumn: dxDataGridColumn) => {
                if (this.MEASUREMENT_COLUMN_NAME_DATA_TYPE_DICT.containsKey(gridColumn.dataField)) {
                    return this.MEASUREMENT_COLUMN_NAME_DATA_TYPE_DICT.tryGetTypedValue<Enums.DataType>(gridColumn.dataField)
                }

                if (_.isFinite(gridColumn.dataField)) {
                    return Number(gridColumn.dataField)
                }

                return null
            })
            .filter((dataType: Enums.DataType) => dataType != null)
            .value()

        return this.userSessionService
            .getDataProvider()
            .DataType
            .getDataTypes(_.uniq(dataTypesFromColumns))
    }

    private getGridColumnCaptionForDataType(gridColumns: ReadonlyArray<dxDataGridColumn>, dataType: Enums.DataType): string {
        let dataField: string = null
        const pair: KeyValuePair<Enums.DataType> = _.find(this.MEASUREMENT_COLUMN_NAME_DATA_TYPE_DICT.toArray(), (pair: KeyValuePair<Enums.DataType>) => {
            return pair.value === dataType
        })

        if (pair != null) {
            dataField = pair.key as string
        }
        else {
            dataField = dataType.toString()
        }

        const column: dxDataGridColumn = _.find(gridColumns, (column: dxDataGridColumn) => {
            return column.dataField === dataField
        })

        if (column != null) {
            return column.caption
        }

        return null
    }

    //#endregion

    //#region Measurements Request

    public getMeasurements(options: GetMeasurementsOptions): DataProviderRequest<MeasureCollection[]> {
        if (options != null && _.size(options.dataTypes) > 0) {
            if (options.latest === true) {
                if (_.size(options.locationIds) > 0) {
                    return this.userSessionService.getDataProvider().Measurements.getLatestMeasurementsFilter(options.locationIds, null, null, options.dataTypes)
                }

                if (_.size(options.deviceIds) > 0) {
                    return this.userSessionService.getDataProvider().Measurements.getLatestMeasurementsFilter(null, options.deviceIds, null, options.dataTypes)
                }

                if (_.size(options.meterIds) > 0) {
                    return this.userSessionService.getDataProvider().Measurements.getLatestMeasurementsFilter(null, null, options.meterIds, options.dataTypes)
                }
            } else {
                if (_.size(options.locationIds) > 0) {
                    return this.userSessionService.getDataProvider().Measurements.getMeasurementsFilter(options.locationIds, null, null, options.dataTypes, options.dateFrom, options.dateTo)
                }

                if (_.size(options.deviceIds) > 0) {
                    return this.userSessionService.getDataProvider().Measurements.getMeasurementsFilter(null, options.deviceIds, null, options.dataTypes, options.dateFrom, options.dateTo)
                }

                if (_.size(options.meterIds) > 0) {
                    return this.userSessionService.getDataProvider().Measurements.getMeasurementsFilter(null, null, options.meterIds, options.dataTypes, options.dateFrom, options.dateTo)
                }
            }
        }

        return DataProviderRequest.fromValue([])
    }

    //#endregion

    public getThresholdColumns(): JQueryPromise<ColumnModel[]> {
        const columns: ColumnModel[] = []

        return this.userSessionService
            .getDataProvider()
            .DataTypeGroup
            .getDataTypeInGroup(Enums.DataTypeGroup.WEBSIMAX_MEASUREMENT_THRESHOLD)
            .then((dataTypesInGroup: OpDataTypeInGroup[]) => {
                if (_.size(dataTypesInGroup) > 0) {
                    const idDataTypes: number[] = _.map(dataTypesInGroup, (dtInGr: OpDataTypeInGroup) => dtInGr.idDataType)

                    return this.userSessionService
                        .getDataProvider()
                        .DataType
                        .getDataTypes(idDataTypes)
                        .then((dataTypes: OpDataType[]) => {
                            if (_.size(dataTypes) > 0) {
                                _.forEach(dataTypes, (dataType: OpDataType) => {
                                    const column: ColumnModel = new ColumnModel()
                                    column.chartType = this.chartTypeToDevExpressType(Enums.ChartType.Line)
                                    column.dataType = dataType
                                    column.id = dataType.idDataType
                                    column.idName = dataType.idDataType.toString()
                                    column.isEditable = dataType.isEditable
                                    column.name = dataType.name
                                    column.type = dataType.idReferenceType
                                    column.caption = dataType.toString()
                                    column.unitId = dataType.idUnit
                                    column.unitName = dataType.unit != null ? dataType.unit.toString() : TypeHelper.emptyString

                                    columns.push(column)
                                })

                                return columns
                            }

                            return columns
                        })
                }

                return columns
            })
    }

}